import { z } from 'zod';

import { BR_STATES } from '../constants';
import { isValidCpfOrCnpj, normalizeDocument } from '../utils/document';
import { describeNfseChar, nfseForbiddenChars } from '../utils/nfse-text';

// =============================================================================
//  Client schemas — Story 2.1 (CRUD)
//  DR-012 CNPJ alfanumérico, DR-014 Cliente unificado, ADR-009 Optimistic locking
// =============================================================================

export const clientStatusSchema = z.enum([
  'lead',
  'ativo',
  'em_implantacao',
  'em_teste',
  'inadimplente',
  'suspenso',
  'cancelado',
  'merged',
  'pending_link',
]);
export type ClientStatusSchema = z.infer<typeof clientStatusSchema>;

export const clientTypeSchema = z.enum(['pessoa_fisica', 'pessoa_juridica']);
export type ClientTypeSchema = z.infer<typeof clientTypeSchema>;

export const documentTypeSchema = z.enum(['CPF', 'CNPJ', 'PASSPORT', 'OTHER']);
export type DocumentTypeSchema = z.infer<typeof documentTypeSchema>;

export const preferredChannelSchema = z.enum(['whatsapp', 'email', 'ambos']);
export type PreferredChannelSchema = z.infer<typeof preferredChannelSchema>;

export const clientCreatedViaSchema = z.enum([
  'control_center_admin',
  'saas_direct',
  'migration',
  'bootstrap_import',
]);
export type ClientCreatedViaSchema = z.infer<typeof clientCreatedViaSchema>;

// Document field normaliza string vazia → null; aceita string ou null/undefined.
const documentField = z
  .string()
  .nullish()
  .transform((value) => {
    if (value === null || value === undefined) return null;
    const normalized = normalizeDocument(value);
    return normalized.length === 0 ? null : normalized;
  });

const ufField = z
  .string()
  .length(2)
  .nullish()
  .transform((value) => (value === null || value === undefined ? null : value.toUpperCase()));

/**
 * Base object schema (sem refinements).
 * Usado pra derivar `createClientSchema` (com refinements) e `updateClientSchema` (partial + version).
 */
/**
 * Campo de endereço que vai pra NFS-e (rua, número, complemento, bairro):
 * barra no cadastro o caractere que o Emissor Nacional recusa — antes, o
 * travessão do Oba passava aqui e a prefeitura recusava a NF depois de
 * emitida (2026-09-29). Vale pra tela (resolver) e pra API (mesmo schema).
 */
function nfseAddressField(max: number) {
  return z
    .string()
    .max(max)
    .superRefine((value, ctx) => {
      const chars = nfseForbiddenChars(value);
      if (chars.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `A nota fiscal não aceita ${chars.map(describeNfseChar).join(', ')}. Apague ou troque (ex: travessão por hífen -).`,
        });
      }
    })
    .optional()
    .nullable();
}

export const clientBaseSchema = z.object({
  type: clientTypeSchema,
  name: z.string().min(1, 'Nome obrigatório.').max(200, 'Nome deve ter até 200 caracteres.'),
  trade_name: z.string().max(200).optional().nullable(),
  document: documentField,
  document_type: documentTypeSchema.default('CPF'),
  inscricao_municipal: z.string().max(50).optional().nullable(),
  inscricao_estadual: z.string().max(50).optional().nullable(),
  email: z
    .union([z.string().email('Email inválido.'), z.literal('')])
    .optional()
    .nullable(),
  fiscal_email: z
    .union([z.string().email('Email fiscal inválido.'), z.literal('')])
    .optional()
    .nullable(),
  financial_email: z
    .union([z.string().email('Email financeiro inválido.'), z.literal('')])
    .optional()
    .nullable(),
  phone: z.string().max(30).optional().nullable(),
  whatsapp: z.string().max(30).optional().nullable(),
  financial_whatsapp: z.string().max(30).optional().nullable(),
  preferred_channel: preferredChannelSchema.default('whatsapp'),
  address_street: nfseAddressField(200),
  address_number: nfseAddressField(20),
  address_complement: nfseAddressField(100),
  address_neighborhood: nfseAddressField(100),
  address_city: z.string().max(100).optional().nullable(),
  address_state: ufField,
  address_zip_code: z.string().max(15).optional().nullable(),
  address_country: z.string().length(2).default('BR'),
  responsavel: z.string().max(200).optional().nullable(),
  observations: z.string().max(5000).optional().nullable(),
  observations_fiscal: z.string().max(5000).optional().nullable(),
  status: clientStatusSchema.default('lead'),
  is_iss_exempt: z.boolean().default(false),
  iss_exempt_reason: z.string().max(500).optional().nullable(),
  skip_dunning: z.boolean().default(false),
  // Fase 2 — DR-036 override fiscal per-client
  fiscal_document_override: z
    .string()
    .regex(/^[A-Z0-9]+$/i, 'Documento aceita só letras e números.')
    .transform((s) => s.replace(/\D/g, '').toUpperCase())
    .refine((s) => s.length === 11 || s.length === 14, 'CPF=11 ou CNPJ=14 caracteres.')
    .optional()
    .nullable(),
});

/**
 * Refinements compartilhados entre create e update.
 * Aplicados via `superRefine` ao schema final.
 */
type ClientBaseShape = z.infer<typeof clientBaseSchema>;
type PartialClientBaseShape = Partial<ClientBaseShape>;

function refineClientPayload(
  data: PartialClientBaseShape,
  ctx: z.RefinementCtx,
  options: { requireDocumentFields: boolean },
): void {
  // CPF/CNPJ obrigatório se status != lead (apenas pra create; no update validamos quando documento ou status são informados)
  if (options.requireDocumentFields) {
    if (data.status && data.status !== 'lead' && !data.document) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['document'],
        message: 'Documento obrigatório quando status diferente de lead.',
      });
    }
  }

  // Validação algoritmo CPF/CNPJ (sempre que document e document_type CPF/CNPJ foram fornecidos)
  if (data.document && (data.document_type === 'CPF' || data.document_type === 'CNPJ')) {
    if (!isValidCpfOrCnpj(data.document)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['document'],
        message: 'Documento inválido (dígito verificador não bate).',
      });
    }
  }

  // UF brasileira válida quando country = BR e UF informada
  if (data.address_country === 'BR' && data.address_state) {
    if (!BR_STATES.includes(data.address_state as (typeof BR_STATES)[number])) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['address_state'],
        message: 'UF inválida. Use uma das 27 UFs brasileiras.',
      });
    }
  }
}

export const createClientSchema = clientBaseSchema.superRefine((data, ctx) => {
  refineClientPayload(data, ctx, { requireDocumentFields: true });
});

/**
 * Tipo do DTO de criação:
 * - Usa `z.output` para refletir o que o service recebe APÓS `parse()` (campos transformados,
 *   defaults aplicados — `address_country` vira `'BR'`, `preferred_channel` vira `'whatsapp'` etc).
 * - Para frontend que precisa do input bruto (campos opcionais sem defaults), use `CreateClientInput`.
 */
export type CreateClientDto = z.output<typeof createClientSchema>;
export type CreateClientInput = z.input<typeof createClientSchema>;

/**
 * Update schema: parte do shape base (parcial) + version obrigatória pra optimistic locking.
 * NOTA: não derivamos via `_def.schema` (API interna do Zod) — usamos `clientBaseSchema.partial()`.
 */
export const updateClientSchema = clientBaseSchema
  .partial()
  .extend({
    version: z.number().int().positive('version obrigatório (optimistic locking).'),
  })
  .superRefine((data, ctx) => {
    refineClientPayload(data, ctx, { requireDocumentFields: false });
  });

export type UpdateClientDto = z.output<typeof updateClientSchema>;
export type UpdateClientInput = z.input<typeof updateClientSchema>;

/**
 * Query schema para GET /api/clients — cursor pagination + filtros.
 */
export const queryClientsSchema = z.object({
  cursor: z.string().uuid('cursor deve ser UUID válido.').optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: clientStatusSchema.optional(),
  created_via: clientCreatedViaSchema.optional(),
  q: z.string().min(1).max(200).optional(),
});
export type QueryClientsDto = z.infer<typeof queryClientsSchema>;

/**
 * Cancel schema — soft delete via PATCH/DELETE com reason opcional e version obrigatória.
 */
export const cancelClientSchema = z.object({
  version: z.number().int().positive('version obrigatório (optimistic locking).'),
  reason: z.string().max(500).optional(),
});
export type CancelClientDto = z.infer<typeof cancelClientSchema>;
